import React, { Fragment, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import PlaceIcon from '@material-ui/icons/Place';

import { RootState } from '../../../../../redux/root';
import SwitchCheckbox from '../../Common/Controls/SwitchCheckbox';
import { setBottomDrawerOpened } from '../../../../../redux/slices/generalSlice';
import { ModalDefaultDraggable } from '../../../../../molecules';
import PayPopup from './PayPopup';
import MobileRow from './MobileRow';
import TableHeaderCell from '../../Common/Table/Header/TableHeaderCell';
import { sortTableHeader } from '../../../utils';
import ViewInvoiceModal from '../../../../../Components/InvoiceTool/ViewInvoiceModal';
import ViewInvoiceEye from './ViewInvoiceEye';
import InvoiceTool from '../../../../../Components/InvoiceTool/InvoiceTool';
import useShowInvoicePdf from '../../../../../Components/InvoiceTool/useShowInvoicePdf';
import { setSelectedInvoicesToPay } from '../../../../../redux/slices/feesSlice';
import { getInvoiceBalance } from '../../../../../Components/InvoiceTool/InvoiceUtil';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const tonePalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: '#E8F6EE', fg: '#196A3B', border: '#C5E7D2' },
  warning: { bg: '#FFF6E0', fg: '#8A5F05', border: '#FFE7AC' },
  info: { bg: '#E8F2FD', fg: '#1E64BF', border: '#CFE6F3' },
  danger: { bg: '#FDECEC', fg: '#9A2523', border: '#F9C9C9' },
  neutral: { bg: '#F2F4F7', fg: '#3B424E', border: '#E6E9EF' },
};

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s === 'paid' || s === 'complete' || s === 'completed') return 'success';
  if (s === 'overdue' || s === 'void' || s === 'failed') return 'danger';
  if (s === 'pending' || s === 'open' || s === 'unpaid') return 'warning';
  if (s.includes('process')) return 'info';
  return 'neutral';
};

const InvoicesItems = () => {
  const { selectedProjectInvoice, selectedInvoicesToPay } = useSelector((s: RootState) => s.fees);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [invoices, setInvoices] = useState([]);
  const [sortedFees, setSortedFees] = useState([]);
  const { tableSortingDirection, tableSortingKey, bottomDrawerOpened } = useSelector((s: RootState) => s.general);
  const scrollRef = useRef<HTMLDivElement>();
  const dispatch = useDispatch();

  const [selectedFeesIds, setSelectedFeesIds] = useState<number[]>([]);
  const { handleInvoiceCellClick, reimbursableAttachment } = useShowInvoicePdf(selectedFeesIds);

  useEffect(() => {
    if (selectedProjectInvoice && selectedProjectInvoice.permit) {
      setInvoices(selectedProjectInvoice.permit.invoices);
      setSortedFees(selectedProjectInvoice.permit.invoices);
    }
  }, [selectedProjectInvoice.permit]);

  useEffect(() => {
    dispatch(setSelectedInvoicesToPay({}));
    return () => {
      dispatch(setSelectedInvoicesToPay({}));
    };
  }, []);

  useEffect(() => {
    if (invoices.length === 0) return;
    const _fees = sortTableHeader(tableSortingDirection, tableSortingKey, [...invoices]);
    setSortedFees(_fees);
  }, [tableSortingDirection, tableSortingKey]);

  const checkAllItem = (check: boolean) => {
    setSelectAll(check);
    const allItems = selectedProjectInvoice.permit.invoices.reduce((itemsObj, fee: any) => {
      return {
        ...itemsObj,
        [fee.id]: fee.paidAt !== '' ? false : check,
      };
    }, {});
    dispatch(setSelectedInvoicesToPay(allItems));
  };

  const setSelectedItem = (e: any, id: number) => {
    const { checked } = e.target;
    const _checkedItems = {
      ...selectedInvoicesToPay,
      [id]: checked,
    };
    dispatch(setSelectedInvoicesToPay(_checkedItems));
  };

  const payInvoices = () => dispatch(setBottomDrawerOpened(true));
  const onCancel = () => dispatch(setBottomDrawerOpened(false));

  const selectedCount = Object.keys(selectedInvoicesToPay).filter(k => selectedInvoicesToPay[k]).length;
  const payButtonDisabled = selectedCount === 0;

  return (
    <Root>
      <HiddenInvoiceStage aria-hidden="true">
        <InvoiceTool fees={selectedFeesIds} reimbursableAttachment={reimbursableAttachment} />
      </HiddenInvoiceStage>
      <ViewInvoiceModal />

      <HeaderCard>
        <HeaderCol>
          <HeaderLabel>Project / Permit</HeaderLabel>
          <HeaderTitle title={selectedProjectInvoice?.projectPermit || ''}>
            {selectedProjectInvoice?.projectPermit || '—'}
          </HeaderTitle>
        </HeaderCol>
        <HeaderDivider />
        <HeaderCol>
          <HeaderLabel>Address</HeaderLabel>
          <HeaderAddress title={selectedProjectInvoice?.projectAddress || ''}>
            <PlaceIcon style={{ fontSize: 16 }} />
            <span>{selectedProjectInvoice?.projectAddress || '—'}</span>
          </HeaderAddress>
        </HeaderCol>
      </HeaderCard>

      <Toolbar>
        <ToolbarLeft>
          <ToolbarEyebrow>Invoices</ToolbarEyebrow>
          <ToolbarMeta>
            <strong>{sortedFees.length}</strong> total
            {selectedCount > 0 && (
              <>
                &nbsp;·&nbsp;<Selected>{selectedCount} selected</Selected>
              </>
            )}
          </ToolbarMeta>
        </ToolbarLeft>
        {/* EA-1344: Pay button enabled. Active once at least one unpaid
            invoice is selected; opens the Pay Invoices modal. */}
        <PayBtn type="button" onClick={payInvoices} disabled={payButtonDisabled}>
          {selectedCount > 0 ? `Pay ${selectedCount} ${selectedCount === 1 ? 'invoice' : 'invoices'}` : 'Pay'}
        </PayBtn>
      </Toolbar>

      <TableCard>
        <THead>
          <ThCell $center>
            <SwitchCheckbox
              name="CheckAll"
              value=""
              title="Select all unpaid invoices"
              onChange={e => checkAllItem(e.target.checked)}
              isRadio={false}
              label=""
              disabled={false}
              checked={selectAll}
            />
          </ThCell>
          <ThCell>
            <TableHeaderCell label="Invoice #" itemName="" hideFilter sortingKey="invoiceNumber" theKey="invoiceNumber" />
          </ThCell>
          <ThCell>
            <TableHeaderCell label="Amount" itemName="" hideFilter sortingKey="total" theKey="total" />
          </ThCell>
          <ThCell>
            <TableHeaderCell label="Status" itemName="" hideFilter sortingKey="status" theKey="status" />
          </ThCell>
          <ThCell>
            <TableHeaderCell label="Date Paid" itemName="" hideFilter sortingKey="paidAt" theKey="paidAt" />
          </ThCell>
          <ThCell $center>
            <ViewLabel>View</ViewLabel>
          </ThCell>
        </THead>

        <TBody ref={scrollRef}>
          {sortedFees.length === 0 && <Empty>No invoices on this permit yet.</Empty>}
          {sortedFees.map((fee, index) => {
            const tone = toneByStatus(fee.status);
            const pal = tonePalette[tone];
            const isSelected = !!selectedInvoicesToPay[fee.id];
            return (
              <Fragment key={index}>
                <MobileRow isOdd={index % 2 > 0} key={index} fee={fee} setSelectedItem={setSelectedItem} />
                <Tr $selected={isSelected}>
                  <TdCell $center>
                    {fee.status !== 'Pending' ? (
                      <Spacer />
                    ) : (
                      <SwitchCheckbox
                        name=""
                        value={`${selectedInvoicesToPay[fee.id]}`}
                        title="Select invoice"
                        onChange={e => setSelectedItem(e, fee.id)}
                        isRadio={false}
                        label=""
                        disabled={false}
                        checked={!!selectedInvoicesToPay[fee.id]}
                      />
                    )}
                  </TdCell>
                  <TdCell>
                    <Mono>{(fee.invoiceNumber || '').split('----')[0] || '—'}</Mono>
                  </TdCell>
                  <TdCell>
                    <Amount>${getInvoiceBalance(fee)}</Amount>
                  </TdCell>
                  <TdCell>
                    <StatusChip style={{ background: pal.bg, color: pal.fg, borderColor: pal.border }}>{fee.status || '—'}</StatusChip>
                  </TdCell>
                  <TdCell>{fee.paidAt ? format(new Date(fee.paidAt), 'MM/dd/yyyy') : '—'}</TdCell>
                  <TdCell $center>
                    <ViewInvoiceEye
                      invoice={fee}
                      setSelectedFeesIds={setSelectedFeesIds}
                      handleInvoiceCellClick={handleInvoiceCellClick}
                    />
                  </TdCell>
                </Tr>
              </Fragment>
            );
          })}
        </TBody>
      </TableCard>

      {bottomDrawerOpened && (
        <ModalDefaultDraggable
          title="Pay Invoices"
          onClose={onCancel}
          centered
          zIndex={20}
          onClickAwayClose={false}
        >
          <PayPopup onCancel={onCancel} />
        </ModalDefaultDraggable>
      )}
    </Root>
  );
};

export default InvoicesItems;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const HiddenInvoiceStage = styled.div`
  position: fixed;
  left: -100000px;
  top: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToolbarEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const ToolbarMeta = styled.span`
  font-size: 0.8125rem;
  color: var(--neutral-600);

  strong {
    color: var(--mainText);
    font-weight: 700;
  }
`;

const Selected = styled.span`
  color: var(--primary-700);
  font-weight: 600;
`;

const PayBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

const TableCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const gridTemplate = '70px minmax(0, 1.3fr) 140px 170px 140px 90px';

const THead = styled.div`
  display: grid;
  grid-template-columns: ${gridTemplate};
  background: var(--neutral-100);
  border-bottom: 1px solid var(--border);

  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const ThCell = styled.div<{ $center?: boolean }>`
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  min-width: 0;

  /* Align the shared TableHeaderCell's internal button (which has its own 14/16 padding) */
  > div {
    width: 100%;
  }
`;

const ViewLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const TBody = styled.div`
  max-height: 48vh;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Tr = styled.div<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: ${gridTemplate};
  border-bottom: 1px solid var(--border);
  background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'transparent')};
  transition: background-color 0.15s ease;
  align-items: center;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ $selected }) => ($selected ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  @media only screen and (max-width: 840px) {
    display: none;
  }
`;

const TdCell = styled.div<{ $center?: boolean }>`
  padding: 12px 12px;
  font-size: 0.875rem;
  color: var(--mainText);
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Mono = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  background: var(--neutral-100);
  border: 1px solid var(--border);
  padding: 2px 8px;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const Amount = styled.span`
  font-weight: 700;
  color: var(--success-700, #196a3b);
  font-variant-numeric: tabular-nums;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
`;

const Spacer = styled.div`
  width: 100%;
  height: 22px;
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

/* Backwards-compatible exports used by ViewInvoiceEye and MobileRow */
export const Red = styled.div`
  color: var(--danger-500, #e0423f);
`;

export const IconWrapper = styled.div<{ hasPdf: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: ${({ hasPdf }) => (hasPdf ? 'var(--neutral-500)' : 'var(--danger-500, #e0423f)')};
  cursor: ${({ hasPdf }) => (hasPdf ? 'pointer' : 'default')};
  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 20px;
  }

  a {
    display: inline-flex;
    color: inherit;
    text-decoration: none;
  }

  &:hover {
    background: ${({ hasPdf }) => (hasPdf ? 'var(--primary-50)' : 'transparent')};
    color: ${({ hasPdf }) => (hasPdf ? 'var(--primary-600)' : 'var(--danger-500, #e0423f)')};
  }
`;

/* Legacy exports kept for backwards compatibility (if any other file imports them) */
export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Header = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
`;

export const HeaderCell = styled.div`
  display: flex;
  align-items: center;
`;

export const ItemsContainer = styled.div`
  max-height: 48vh;
  overflow-y: auto;
`;
